import i18n from './i18n'
import { loadVuetifyLocale } from './vuetify'
import { APP_LANGUAGES } from '@/utils/constants'

// Timeout for language loading (10 seconds)
const LOAD_TIMEOUT_MS = 10000

// Track which locales have been loaded
const loadedLocales = new Set(['en']) // English is always loaded

// Track loading promises per locale to handle concurrent requests
const loadingPromises = new Map()

/**
 * Create a promise that rejects after a timeout
 * @param {number} ms - Timeout in milliseconds
 * @returns {Promise} Promise that rejects with timeout error
 */
function createTimeoutPromise(ms) {
  return new Promise((_, reject) =>
    setTimeout(() => reject(new Error('LANGUAGE_LOAD_TIMEOUT')), ms)
  )
}

/**
 * Set the HTML lang attribute for proper CSS selectors and font loading
 * This is centralized here to avoid duplication across the codebase.
 * @param {string} locale - The locale code to set (e.g., 'en', 'ar', 'de')
 */
export function setHtmlLang(locale) {
  document.documentElement.lang = locale
}

/**
 * Check if a locale is already loaded
 * @param {string} locale - The locale code to check
 * @returns {boolean} True if locale is loaded
 */
export function isLocaleLoaded(locale) {
  return loadedLocales.has(locale)
}

/**
 * Load a locale's messages dynamically with timeout protection
 * @param {string} locale - The locale code to load (e.g., 'de', 'ar')
 * @returns {Promise<{success: boolean, timedOut: boolean}>} Result object with success and timeout status
 */
export async function loadLocaleMessages(locale) {
  // If already loaded, return immediately
  if (loadedLocales.has(locale)) {
    return { success: true, timedOut: false }
  }

  // If already loading this specific locale, wait for that promise
  if (loadingPromises.has(locale)) {
    return loadingPromises.get(locale)
  }

  // Start loading - dynamic imports create separate chunks
  const loadPromise = (async () => {
    try {
      // Validate locale against supported languages
      if (!APP_LANGUAGES.includes(locale)) {
        console.warn(`Unknown locale: ${locale}, falling back to English`)
        return { success: false, timedOut: false }
      }

      // Race between loading and timeout
      const loadingTask = (async () => {
        // Dynamic import with webpack magic comment for chunk naming
        const messages = await import(
          /* webpackChunkName: "lang-[request]" */
          `@/lang/${locale}/index.js`
        )

        // Load Vuetify locale before marking as complete
        // This ensures Vuetify components like data tables are also translated
        await loadVuetifyLocale(locale)

        return messages
      })()

      const messages = await Promise.race([
        loadingTask,
        createTimeoutPromise(LOAD_TIMEOUT_MS)
      ])

      // Set the loaded messages in i18n
      i18n.global.setLocaleMessage(locale, messages.default)
      loadedLocales.add(locale)

      return { success: true, timedOut: false }
    } catch (error) {
      if (error.message === 'LANGUAGE_LOAD_TIMEOUT') {
        console.error(`Timeout loading locale ${locale} after ${LOAD_TIMEOUT_MS}ms`)
        return { success: false, timedOut: true }
      }
      console.error(`Failed to load locale ${locale}:`, error)
      return { success: false, timedOut: false }
    } finally {
      loadingPromises.delete(locale)
    }
  })()

  loadingPromises.set(locale, loadPromise)
  return loadPromise
}

/**
 * Set the active locale, loading it first if necessary.
 * Sets the HTML lang attribute immediately for CSS selectors and font loading.
 * @param {string} locale - The locale code to set as active
 * @returns {Promise<{success: boolean, timedOut: boolean}>} Result object with success and timeout status
 */
export async function setLocale(locale) {
  // Set lang attribute immediately for CSS effect (before loading begins)
  setHtmlLang(locale)

  const result = await loadLocaleMessages(locale)

  if (result.success) {
    i18n.global.locale = locale
    return result
  }

  // Failed to load, revert lang attribute to English
  setHtmlLang('en')
  console.warn(`Failed to set locale ${locale}, keeping current locale`)
  return result
}

export default {
  setHtmlLang,
  isLocaleLoaded,
  loadLocaleMessages,
  setLocale
}
